class CalmEdenHeader {
  constructor(header) {
    this.header = header;
    this.drawer = header.querySelector('.ce-header__drawer');
    this.summary = this.drawer?.querySelector('summary');
    this.links = header.querySelectorAll('.ce-header__mobile-nav a');
    this.links.forEach((link) => link.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer?.open) {
        event.preventDefault();
        this.close(true);
      }
    });
  }

  close(returnFocus = false) {
    if (this.drawer) this.drawer.removeAttribute('open');
    if (returnFocus) this.summary?.focus();
  }
}

class NaturalSleepDropsProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.nsd-product-form');
    this.variantControl = section.querySelector('[data-variant-id]');
    this.price = section.querySelector('[data-product-price]');
    this.status = section.querySelector('[data-product-status]');
    this.addButton = section.querySelector('[data-add-to-cart]');
    this.quantity = section.querySelector('input[name="quantity"]');
    this.variantData = section.querySelector('[data-product-variants]');
    this.variants = this.parseVariants();
    this.dialog = section.querySelector('[data-media-dialog]');
    this.dialogContent = section.querySelector('[data-media-dialog-content]');
    this.purchaseLocked = section.dataset.purchaseLocked === 'true';
    this.activeVariant = this.variants.find((variant) => String(variant.id) === String(this.variantControl?.value));
    section.querySelector('.nsd-product-form__errors')?.focus();
    this.labels = {
      comingSoon: section.querySelector('[data-label-coming-soon]')?.textContent.trim() || 'Coming soon',
      comingSoonMessage: section.querySelector('[data-label-coming-soon-message]')?.textContent.trim() || '',
      add: section.querySelector('[data-label-add]')?.textContent.trim() || 'Add to cart',
      soldOut: section.querySelector('[data-label-sold-out]')?.textContent.trim() || 'Sold out',
      available: section.querySelector('[data-label-available]')?.textContent.trim() || 'Available',
      unavailable: section.querySelector('[data-label-unavailable]')?.textContent.trim() || 'Unavailable'
    };
    this.bindVariantControl();
    this.bindGallery();
    this.bindDialog();
    this.bindFormGuard();
    if (this.activeVariant) this.updatePurchaseState(this.activeVariant);
  }

  parseVariants() {
    if (!this.variantData) return [];
    try {
      const variants = JSON.parse(this.variantData.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }

  bindVariantControl() {
    if (!this.variantControl || this.variantControl.tagName !== 'SELECT') return;
    this.variantControl.addEventListener('change', () => this.onVariantChange());
  }

  onVariantChange() {
    const variant = this.variants.find((candidate) => String(candidate.id) === String(this.variantControl.value));
    if (!variant) {
      this.activeVariant = null;
      this.updateUnavailableState();
      return;
    }
    this.activeVariant = variant;
    this.updatePurchaseState(variant);
    if (variant.featuredMediaId) this.showMedia(variant.featuredMediaId);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updatePurchaseState(variant) {
    const comingSoon = this.purchaseLocked || Number(variant.price) <= 0;
    const canPurchase = variant.available && !comingSoon;
    this.price.textContent = comingSoon ? this.labels.comingSoon : variant.formattedPrice;
    this.addButton.textContent = comingSoon ? this.labels.comingSoon : (variant.available ? this.labels.add : this.labels.soldOut);
    this.addButton.disabled = !canPurchase;
    this.quantity.disabled = !canPurchase;
    this.status.textContent = comingSoon ? this.labels.comingSoonMessage : (variant.available ? this.labels.available : this.labels.unavailable);
  }

  updateUnavailableState() {
    this.addButton.textContent = this.labels.unavailable;
    this.addButton.disabled = true;
    this.quantity.disabled = true;
    this.status.textContent = this.labels.unavailable;
  }

  bindGallery() {
    this.section.querySelectorAll('[data-media-target]').forEach((button) => {
      button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget));
    });
    this.section.querySelectorAll('[data-media-open]').forEach((button) => {
      button.addEventListener('click', () => this.openMedia(button.closest('[data-media-id]')));
    });
  }

  pauseMedia(container) {
    if (!container) return;
    container.querySelectorAll('video, audio').forEach((media) => media.pause());
    container.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    container.querySelectorAll('iframe').forEach((frame) => {
      const source = frame.getAttribute('src');
      if (!source || !frame.contentWindow) return;
      try {
        const origin = new URL(source, window.location.href).origin;
        if (/youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname)) {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
        }
        if (/vimeo\.com$/.test(new URL(origin).hostname)) {
          frame.contentWindow.postMessage({ method: 'pause' }, origin);
        }
      } catch {
        return;
      }
    });
  }

  showMedia(mediaId) {
    const targetId = String(mediaId);
    this.section.querySelectorAll('[data-media-id]').forEach((item) => {
      const isTarget = String(item.dataset.mediaId) === targetId;
      if (!isTarget) this.pauseMedia(item);
      item.hidden = !isTarget;
    });
    this.section.querySelectorAll('[data-media-target]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.mediaTarget) === targetId ? 'true' : 'false');
    });
  }

  openMedia(item) {
    if (!item || !this.dialog || !this.dialogContent) return;
    const media = item.querySelector('img, video, iframe, model-viewer');
    if (!media) return;
    this.pauseMedia(this.dialogContent);
    this.dialogContent.replaceChildren(media.cloneNode(true));
    if (!this.dialog.open) this.dialog.showModal();
  }

  bindDialog() {
    if (!this.dialog) return;
    this.dialog.querySelector('[data-media-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      this.pauseMedia(this.dialogContent);
      this.dialogContent?.replaceChildren();
    });
  }

  bindFormGuard() {
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      const comingSoon = this.purchaseLocked || !this.activeVariant || Number(this.activeVariant.price) <= 0;
      if (comingSoon || !this.activeVariant.available) {
        event.preventDefault();
        this.status.textContent = comingSoon ? this.labels.comingSoonMessage : this.labels.unavailable;
        this.status.focus();
      }
    });
  }
}

document.querySelectorAll('[data-ce-header]').forEach((header) => new CalmEdenHeader(header));
document.querySelectorAll('[data-natural-sleep-drops-product]').forEach((section) => new NaturalSleepDropsProduct(section));

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-ce-header]').forEach((header) => new CalmEdenHeader(header));
  event.target.querySelectorAll('[data-natural-sleep-drops-product]').forEach((section) => new NaturalSleepDropsProduct(section));
});
